/*
 * COCOSIN — collection pages (Reformation format)
 * Works alongside the theme's collection.js (panel open/close, "Show more")
 * and storefont-filters.js (filter form, results update).
 *
 * Adds:
 * - Filter buttons in the bar ([data-cc-open]) open the panel at their group;
 *   "Sort" opens it at Sort by.
 * - Sort radios in the panel feed the form's hidden sort field, then the panel
 *   closes and the results re-sort.
 * - While filters update, the theme replaces the whole section; the panel is
 *   put back open at the same scroll position, with the same groups unfolded.
 * - "Clear all" in the panel keeps the current sort.
 * - Esc closes the panel; focus moves into it and back to the button. When
 *   the results update, focus goes back to the same tick box or button in the
 *   new markup (keyboard and screen-reader users keep their place).
 * - "24 of 56 styles" and the progress line follow "Show more" (a featured
 *   card from outside the collection is not counted).
 * ?featured= is handled by snippets/cc-featured.liquid (Oct 2026), which runs
 * as soon as the grid is on the page.
 */
(() => {
  if (window.__ccPlp) return;
  window.__ccPlp = true;

  const state = { scroll: 0, groups: {}, reopen: false, trigger: null, triggerSel: null, focusSel: null };

  const section = () => document.querySelector('.cc-plp');
  const esc = (v) => (window.CSS && CSS.escape ? CSS.escape(String(v)) : String(v).replace(/["\\]/g, '\\$&'));

  // A selector that finds the same control again after the section is replaced.
  const selectorFor = (el) => {
    if (!(el instanceof Element)) return null;
    if (el.closest('facet-remove')) return '.cc-plp__bar .m-sidebar--open';
    if (el.matches('input[name]')) {
      const byValue = el.type === 'checkbox' || el.type === 'radio';
      return `.cc-plp input[name="${esc(el.name)}"]${byValue ? `[value="${esc(el.value)}"]` : ''}`;
    }
    if (el.matches('[data-cc-open]')) return `.cc-plp__bar [data-cc-open="${esc(el.dataset.ccOpen)}"]`;
    if (el.matches('.m-sidebar--open')) return '.cc-plp__bar .m-sidebar--open';
    if (el.matches('summary')) {
      const d = el.closest('[data-cc-group]');
      return d ? `.cc-plp [data-cc-group="${esc(d.dataset.ccGroup)}"] > summary` : null;
    }
    if (el.matches('.cc-plp__close, .cc-plp__clear')) return '.cc-plp .cc-plp__close';
    if (el.matches('.cc-plp__apply')) return '.cc-plp .cc-plp__apply';
    return null;
  };

  const find = (sel) => {
    if (!sel) return null;
    try {
      return document.querySelector(sel);
    } catch (_) {
      return null;
    }
  };

  // The button that opened the panel, or its copy in the re-rendered bar.
  const triggerEl = () => {
    const t = state.trigger;
    if (t && t.isConnected) return t;
    return find(state.triggerSel) || find('.cc-plp__bar .m-sidebar--open');
  };
  const setTrigger = (el) => {
    state.trigger = el || null;
    state.triggerSel = el ? selectorFor(el) : null;
  };
  const theme = () => window.MinimogTheme && window.MinimogTheme.Collection;
  const isOpen = () => {
    const s = section();
    return !!(s && s.classList.contains('sidebar-open'));
  };

  // Run once the theme's Collection controller exists (it starts on window load).
  const whenReady = (fn) => {
    const c = theme();
    if (c && c.domNodes) return fn(c);
    window.addEventListener('load', () => setTimeout(() => theme() && fn(theme()), 0), { once: true });
  };

  const focusClose = () => {
    const s = section();
    const btn = s && s.querySelector('.cc-plp__close');
    if (btn) btn.focus({ preventScroll: true });
  };

  const openAt = (key, trigger) =>
    whenReady((c) => {
      const s = section();
      if (!s) return;
      setTrigger(trigger);
      let group = null;
      if (key) {
        group = s.querySelector(`[data-cc-group="${window.CSS && CSS.escape ? CSS.escape(key) : key}"]`);
        if (group && group.tagName === 'DETAILS') group.open = true;
      }
      c.openSidebarFilter();
      requestAnimationFrame(() => {
        const body = s.querySelector('[data-cc-body]');
        if (body) body.scrollTop = group && key !== 'sort' ? group.offsetTop : 0;
        focusClose();
      });
    });

  const close = () =>
    whenReady((c) => {
      if (!isOpen()) return;
      c.closeSidebarFilter();
      const t = triggerEl();
      setTrigger(null);
      if (t) t.focus({ preventScroll: true });
    });

  const form = () => document.getElementById('CollectionFiltersForm');

  // ---------------------------------------------------------------- clicks
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element) || !t.closest('.cc-plp')) return;

    const opener = t.closest('[data-cc-open]');
    if (opener) {
      e.preventDefault();
      openAt(opener.dataset.ccOpen, opener);
      return;
    }

    const all = t.closest('.m-sidebar--open');
    if (all) {
      // collection.js opens the panel; move focus into it.
      setTrigger(all);
      requestAnimationFrame(focusClose);
      return;
    }

    if (t.closest('[data-cc-close]')) {
      e.preventDefault();
      close();
      return;
    }

    const clear = t.closest('[data-cc-clear]');
    if (clear) {
      e.preventDefault();
      const f = document.querySelector('collection-filters-form');
      if (f && typeof f.renderPage === 'function') f.renderPage(clear.getAttribute('data-cc-clear') || '');
      return;
    }

    // The panel's own close button and the dimmed backdrop are handled by
    // collection.js; send focus back to the button that opened the panel.
    if (t.closest('.m-sidebar--close') || t.classList.contains('cc-plp__drawer')) {
      const back = triggerEl();
      setTrigger(null);
      if (back) setTimeout(() => back.focus({ preventScroll: true }), 0);
    }
  });

  // ---------------------------------------------------------------- focus
  // A tick box, sort choice or chip that updates the results makes the theme
  // replace the whole section, and the control that had focus disappears. Note
  // which control had focus when the update started; restoreFocus() then puts
  // focus on its copy. Nothing is noted when focus was outside the section
  // (e.g. a mouse click in Safari), so a mouse user's focus is not moved.
  const noteFocus = () => {
    const a = document.activeElement;
    state.focusSel = a instanceof Element && a !== document.body && a.closest('.cc-plp') ? selectorFor(a) : null;
  };
  ['input', 'change'].forEach((type) =>
    document.addEventListener(
      type,
      (e) => {
        if (e.target instanceof Element && e.target.closest('.cc-plp')) noteFocus();
      },
      true
    )
  );
  document.addEventListener(
    'click',
    (e) => {
      if (e.target instanceof Element && e.target.closest('.cc-plp facet-remove, .cc-plp [data-cc-clear]')) noteFocus();
    },
    true
  );

  const restoreFocus = (s, reopened) => {
    const sel = state.focusSel;
    state.focusSel = null;
    const a = document.activeElement;
    if (!sel || (a && a !== document.body && a.isConnected)) return;
    let el = find(sel);
    if (el && (el.disabled || (!reopened && el.closest('.cc-plp__drawer')))) el = null;
    if (!el) el = s.querySelector(reopened ? '.cc-plp__close' : '.cc-plp__bar .m-sidebar--open');
    if (el) el.focus({ preventScroll: true });
  };

  // ---------------------------------------------------------------- sort
  document.addEventListener('change', (e) => {
    const radio = e.target;
    if (!(radio instanceof HTMLInputElement) || !radio.matches('[data-cc-sort]')) return;
    const f = form();
    const select = f && f.querySelector('[data-form-sorting] select');
    if (!select) return;
    select.selectedIndex = Number(radio.dataset.index) || 0;
    close();
    f.dispatchEvent(new Event('input'));
  });

  // ---------------------------------------------------------------- keys
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      close();
    }
  });

  // ---------------------------------------------------------------- keep the panel as it was
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (d instanceof HTMLDetailsElement && d.matches('.cc-plp [data-cc-group]')) {
        state.groups[d.dataset.ccGroup] = d.open;
      }
    },
    true
  );

  document.addEventListener(
    'scroll',
    (e) => {
      const b = e.target;
      if (b instanceof Element && b.matches('[data-cc-body]')) state.scroll = b.scrollTop;
    },
    true
  );

  // storefont-filters.js swaps the whole section; note whether the panel was open.
  const watch = () => {
    const s = section();
    const parent = s && s.parentNode;
    if (!parent || parent.__ccWatched) return;
    parent.__ccWatched = true;
    new MutationObserver((records) => {
      records.forEach((r) =>
        r.removedNodes.forEach((n) => {
          if (n.nodeType === 1 && n.classList.contains('cc-plp')) state.reopen = n.classList.contains('sidebar-open');
        })
      );
    }).observe(parent, { childList: true });
  };

  const restoreGroups = (s) => {
    s.querySelectorAll('details[data-cc-group]').forEach((d) => {
      const k = d.dataset.ccGroup;
      if (Object.prototype.hasOwnProperty.call(state.groups, k)) d.open = state.groups[k];
    });
  };

  document.addEventListener('collection:rerendered', () => {
    const s = section();
    const c = theme();
    if (!s) return;
    restoreGroups(s);
    if (state.reopen && c) {
      s.classList.add('cc-instant');
      c.openSidebarFilter();
      requestAnimationFrame(() => {
        const body = s.querySelector('[data-cc-body]');
        if (body) body.scrollTop = state.scroll;
        restoreFocus(s, true);
        if (body) body.scrollTop = state.scroll;
        requestAnimationFrame(() => s.classList.remove('cc-instant'));
      });
    } else {
      restoreFocus(s, false);
    }
    state.reopen = false;
    watch();
    initMore();
    stickyTop();
  });

  // ---------------------------------------------------------------- "24 of 56 styles"
  const initMore = () => {
    const s = section();
    const more = s && s.querySelector('[data-cc-more]');
    const grid = s && s.querySelector('[data-product-container]');
    if (!more || !grid || grid.__ccMore) return;
    grid.__ccMore = true;
    const total = Number(more.dataset.total) || 0;
    const label = more.dataset.label || '';
    const count = more.querySelector('[data-cc-more-count]');
    const bar = more.querySelector('[data-cc-progress]');
    const update = () => {
      const n = grid.querySelectorAll('.m-product-item:not([data-cc-extra])').length;
      if (count && label) count.textContent = label.replace('{n}', n);
      if (bar && total) bar.style.width = `${Math.min(100, (n / total) * 100)}%`;
    };
    new MutationObserver(update).observe(grid, { childList: true });
  };

  // ---------------------------------------------------------------- sticky bar under the sticky header
  // The theme header (<m-header>) is either always stuck ("header-sticky-always")
  // or shows on scroll-up ("stuck" + "scroll-up"). The filter bar sits under it
  // while it shows, and at the very top while it is hidden.
  let headerWatched = false;
  const stickyTop = () => {
    const s = section();
    if (!s) return;
    const header = document.querySelector('m-header');
    let top = 0;
    if (header) {
      const shown =
        header.classList.contains('header-sticky-always') ||
        (header.classList.contains('stuck') && header.classList.contains('scroll-up'));
      if (shown) {
        const wrap = header.querySelector('.m-header__wrapper') || header;
        top = Math.max(0, Math.round(wrap.offsetHeight));
      }
    }
    s.style.setProperty('--cc-sticky-top', `${top}px`);
  };
  const watchHeader = () => {
    const header = document.querySelector('m-header');
    if (!header || headerWatched) return;
    headerWatched = true;
    new MutationObserver(stickyTop).observe(header, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', stickyTop, { passive: true });
  };

  const start = () => {
    watch();
    initMore();
    watchHeader();
    stickyTop();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', start);
  }
})();